<template>
  <div class="container-fluid">
    <!-- Bread-crumb-->
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>
    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <!-- Header -->
          <div class="header d-flex justify-content-between align-items-center">
            <h2>
              IP Blocklist
              <span class="badge badge-primary ml-2" v-if="totalRows">
                {{ totalRows }}
              </span>
            </h2>
            <!-- Right-aligned action buttons -->
            <div class="ml-auto d-flex">
              <b-button v-if="$store.getters['authpermission/hasAnyPermission'](['ipblocklist.create'])" variant="primary" size="sm" @click="showAddIpModal">
                <i class="fa fa-plus mr-1"></i>
                Add IP
              </b-button>
            </div>
          </div>
          <div class="body pt-0">
            <!-- SearchBox -->
            <div
              class="chart-top-action d-lg-flex justify-content-end py-3 fresh-customer-filter"
            >
              <div class="mr-0 mr-lg-3 action-top-search">
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Search IP / reason"
                    aria-label="Search"
                    v-model="search_keywords"
                    v-on:keyup.enter="searchIps"
                    aria-describedby="search-ipblocklist"
                  />
                  <div class="input-group-append" @click="searchIps()">
                    <span class="input-group-text" id="search-ipblocklist">
                      <i class="icon-magnifier"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Active search pill -->
            <div
              class="filter-list-area d-flex flex-wrap align-items-center mb-1"
              v-if="appliedSearch"
            >
              <i class="fa fa-filter text-primary mr-1 mb-2" aria-hidden="true"></i>
              <div class="mb-1">
                <label class="font-14 line-height-18 mb-0 ml-2">Search:</label>
                <span class="badge badge-warning-solid">
                  {{ appliedSearch }}
                  <i class="fa fa-close cursor-pointer ml-1" @click="clearSearch"></i>
                </span>
              </div>
            </div>
            <!-- Table Pagination -->
            <div class="py-1 align-items-center justify-content-end table-pagination">
              <div class="number-page">
                <b-pagination
                  class="pagination-small"
                  v-model="currentPage"
                  :total-rows="totalRows"
                  first-text="First"
                  last-text="Last"
                  :per-page="perPage"
                  align="right"
                ></b-pagination>
              </div>
            </div>
            <!-- Table -->
            <b-table
              responsive
              table-class="mb-0 table-custom border"
              head-variant="light"
              :items="typeof items === 'array' || typeof items === 'object' ? items : []"
              :fields="fields"
              :per-page="perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @sort-changed="onSortChanged"
              striped
              show-empty
            >
              <template #cell(cidr)="data">
                <span class="font-weight-bold">{{ data.value }}</span>
                <i
                  class="fa fa-copy ml-1 cursor-pointer text-muted"
                  v-if="data.value != '-' && data.value != ''"
                  @click="copyText(data.value, 'IP / CIDR')"
                ></i>
              </template>
              <template #cell(type)="data">
                <span
                  class="badge"
                  :class="
                    data.value === 'Single IP'
                      ? 'badge-info-solid'
                      : 'badge-warning-solid'
                  "
                >
                  {{ data.value }}
                </span>
              </template>
              <template #cell(reason)="data">
                <span :title="data.item.fullReason">
                  {{ data.value ? data.value.substring(0, 60) : "-" }}
                  <span v-if="data.value && data.value.length > 60">...</span>
                </span>
              </template>
              <template #cell(addedByName)="data">
                <i class="fa fa-user-o mr-1 text-muted" v-if="data.value"></i>
                {{ data.value ? data.value : "-" }}
              </template>
              <template #cell(createdAt)="data">
                <i class="fa fa-calendar-o mr-1 text-muted"></i>
                {{ data.value }}
              </template>
              <template #cell(action)="data">
                <b-dropdown
                  id="dropdown-action"
                  text="Actions"
                  right
                  variant="outline-primary"
                  size="sm"
                >
                  <template v-if="$store.getters['authpermission/hasAnyPermission'](['ipblocklist.edit'])">
                    <b-dropdown-item @click="editIp(data.item)">
                      <i class="fa fa-pencil mr-1"></i> Edit
                    </b-dropdown-item>
                  </template>
                  <template v-if="$store.getters['authpermission/hasAnyPermission'](['ipblocklist.delete'])">
                    <b-dropdown-item @click="deleteIp(data.item)">
                      <i class="fa fa-trash mr-1"></i> Delete
                    </b-dropdown-item>
                  </template>
                </b-dropdown>
              </template>
              <template #empty>
                <div class="ip-empty-state text-center py-5">
                  <div class="ip-empty-state__icon mb-2">
                    <i class="fa fa-ban"></i>
                  </div>
                  <h5 class="mb-1">No blocked IPs found</h5>
                  <p class="text-muted mb-3">
                    {{
                      appliedSearch
                        ? "No entries match your search. Try a different keyword."
                        : "Add an IP address or CIDR range to start blocking logins."
                    }}
                  </p>
                  <b-button
                    v-if="appliedSearch"
                    variant="outline-secondary"
                    size="sm"
                    @click="clearSearch"
                  >
                    Clear search
                  </b-button>
                  <b-button v-else-if="$store.getters['authpermission/hasAnyPermission'](['ipblocklist.create'])" variant="primary" size="sm" @click="showAddIpModal">
                    <i class="fa fa-plus mr-1"></i> Add IP
                  </b-button>
                </div>
              </template>
            </b-table>
            <div
              class="d-flex py-3 align-items-center justify-content-between table-pagination"
            >
              <div class="per-page">
                <b-form-group label="Per page" label-for="per-page-select" class="mb-0">
                  <b-form-select
                    id="per-page-select"
                    v-model="perPage"
                    :options="pageOptions"
                    size="sm"
                  ></b-form-select>
                </b-form-group>
              </div>
              <div class="number-page">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  first-text="First"
                  last-text="Last"
                  :per-page="perPage"
                  align="right"
                ></b-pagination>
              </div>
              <div class="go-page">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">Go To Page</span>
                  </div>
                  <input
                    type="text"
                    v-model="currentPageJump"
                    class="form-control"
                    value=""
                    @keyup.enter="onEnter"
                    onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit IP Modal -->
    <b-modal
      id="add-ip-modal"
      v-model="showAddIp"
      :title="modaltitle"
      size="lg"
      hide-footer
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <ValidationObserver v-slot="{ passes }">
        <form @submit.prevent="passes(submitForm)" autocomplete="off">
          <b-alert show variant="info" class="d-flex align-items-center">
            <i class="fa fa-info-circle mr-2"></i>
            <span>
              Enter a single IP (stored as <strong>/32</strong> for IPv4 or
              <strong>/128</strong> for IPv6) or a CIDR range, e.g.
              <strong>203.0.113.0/24</strong>.
            </span>
          </b-alert>
          <div class="row">
            <!-- IP / CIDR -->
            <div class="col-md-12">
              <ValidationProvider
                name="IP / CIDR"
                rules="required"
                v-slot="{ errors }"
              >
                <div class="form-group">
                  <label for="ipcidr">
                    IP address or CIDR range
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="ipcidr"
                    v-model="newIp.ip"
                    class="form-control"
                    :class="{ 'is-invalid': errors[0] }"
                    placeholder="e.g. 203.0.113.7 or 203.0.113.0/24"
                  />
                  <span class="text-danger error-msg">{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
            <!-- Reason -->
            <div class="col-md-12">
              <ValidationProvider name="Reason" rules="required" v-slot="{ errors }">
                <div class="form-group">
                  <label for="reason">
                    Reason
                    <span class="text-danger">*</span>
                  </label>
                  <textarea
                    id="reason"
                    v-model="newIp.reason"
                    class="form-control"
                    :class="{ 'is-invalid': errors[0] }"
                    rows="3"
                    placeholder="Why is this IP / range being blocked?"
                  ></textarea>
                  <span class="text-danger error-msg">{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-end mt-4">
            <b-button
              variant="outline-secondary"
              size="lg"
              class="mr-3"
              @click="hideModal"
              :disabled="isSubmitting"
            >
              Cancel
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              v-if="!editMode"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting">
                <b-spinner small></b-spinner>
                Adding...
              </span>
              <span v-else>Add IP</span>
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              v-if="editMode"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting">
                <b-spinner small></b-spinner>
                Updating...
              </span>
              <span v-else>Update IP</span>
            </b-button>
          </div>
        </form>
      </ValidationObserver>
    </b-modal>

    <main-loader :loading="isLoading"></main-loader>
  </div>
</template>

<script>
import { ISLOADING } from "../../../store/mutations.type";
const LoaderComponent =
  require("@/components/admin/loader/LoaderComponent.vue").default;
import { mapGetters } from "vuex";
import {
  GET_IP_BLOCKLIST,
  POST_IP_BLOCKLIST,
  GET_IP_BLOCKLIST_DETAILS,
  UPDATE_IP_BLOCKLIST,
  DELETE_IP_BLOCKLIST,
} from "../../../store/actions.type.js";
import { myFunction } from "@/common/common-helper.js";
import { ValidationObserver, ValidationProvider } from "vee-validate";

const SUCCESS_CODE = "1012-000";

export default {
  name: "IpBlocklistComponent",
  components: {
    BreadCrumb: () => import("@/components/BreadCrumb.vue"),
    "main-loader": LoaderComponent,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Home", links: "/home" },
        { title: "IP Blocklist", isbold: true },
      ],
      search_keywords: "",
      appliedSearch: "",
      items: [],
      fields: [
        { key: "cidr", label: "IP / CIDR", sortable: true },
        { key: "type", label: "Type" },
        { key: "reason", label: "Reason" },
        { key: "addedByName", label: "Added By" },
        { key: "createdAt", label: "Created At", sortable: true },
        { key: "action", label: "Action" },
      ],
      totalRows: 0,
      currentPage: 1,
      currentPageJump: 1,
      perPage: 10,
      pageOptions: [10, 20, 50, 100, { value: 1000, text: 1000 }],
      sortBy: "createdAt",
      sortDesc: true,
      sortDirection: "asc",

      // Add/Edit IP modal state
      showAddIp: false,
      modaltitle: "",
      editMode: false,
      selectIpId: "",
      isSubmitting: false,
      apiError: null,
      newIp: {
        ip: "",
        reason: "",
      },
    };
  },
  methods: {
    searchIps() {
      this.currentPage = 1;
      this.$store.commit(ISLOADING, true);
      this.getIpList();
    },
    clearSearch() {
      this.search_keywords = "";
      this.currentPage = 1;
      this.$store.commit(ISLOADING, true);
      this.getIpList();
    },
    ipType(cidr) {
      if (!cidr) return "Range";
      return cidr.endsWith("/32") || cidr.endsWith("/128") ? "Single IP" : "Range";
    },
    showAddIpModal() {
      this.modaltitle = "Add IP";
      this.editMode = false;
      this.resetForm();
      this.showAddIp = true;
    },
    hideModal() {
      this.showAddIp = false;
    },
    resetForm() {
      this.isSubmitting = false;
      this.newIp = {
        ip: "",
        reason: "",
      };
    },
    async submitForm() {
      if (this.editMode) {
        await this.updateIp();
      } else {
        await this.addIp();
      }
    },
    async addIp() {
      this.isSubmitting = true;
      try {
        const payload = {
          ip: this.newIp.ip,
          reason: this.newIp.reason,
        };

        const response = await this.$store.dispatch(POST_IP_BLOCKLIST, payload);

        if (response.data && response.data.code === SUCCESS_CODE) {
          myFunction.showInfo("IP added to blocklist successfully.", "success");
          this.resetForm();
          this.showAddIp = false;
          this.$store.commit(ISLOADING, true);
          this.getIpList();
        } else {
          this.isSubmitting = false;
          const errorMessage =
            response.data?.message || "Failed to add IP. Please try again.";
          const errorDetail = response.data?.detail;
          this.apiError = errorDetail
            ? `${errorMessage}: ${errorDetail}`
            : errorMessage;
          myFunction.showInfo(this.apiError, "error");
        }
      } catch (error) {
        this.isSubmitting = false;
        console.error("Error adding IP:", error);
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred while adding the IP. Please try again.";
        const errorDetail = error.response?.data?.detail;
        this.apiError = errorDetail
          ? `${errorMessage}: ${errorDetail}`
          : errorMessage;
        myFunction.showInfo(this.apiError, "error");
      }
    },
    copyText(data, type) {
      var m = document;
      data = m.createTextNode(data);
      var w = window;
      var b = m.body;
      var d = {};
      b.appendChild(data);
      d = m.createRange();
      var g = w.getSelection;
      d.selectNodeContents(data);
      g().removeAllRanges();
      g().addRange(d);
      m.execCommand("copy");
      g().removeAllRanges();
      data.remove();
      myFunction.showInfo(`${type} Copied`, "success");
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      if (this.sortBy != "") {
        this.$store.commit(ISLOADING, true);
        this.getIpList();
      }
    },
    async getIpList() {
      let page = this.currentPage != undefined ? this.currentPage : 1;
      let page_size = this.perPage != undefined ? this.perPage : 10;
      let sort = this.sortDesc == true ? "desc" : "asc";
      // Map the table column to the DB column the API can order by.
      let sortby = this.sortBy === "cidr" ? "cidr" : "created_at";

      this.currentPageJump = page;

      let tempparam = `?sort=${sort}&sortby=${sortby}&limit=${page_size}&page=${page}`;
      if (this.search_keywords) {
        tempparam = tempparam + `&search=${this.search_keywords.trimStart()}`;
      }

      const response = await this.$store.dispatch(GET_IP_BLOCKLIST, tempparam);
      this.appliedSearch = this.search_keywords ? this.search_keywords.trim() : "";
      this.items = [];
      const tempitems = [];
      if (response.status == 200) {
        response.data.details.map((item) => {
          tempitems.push({
            id: item.id,
            cidr: item.cidr,
            type: this.ipType(item.cidr),
            reason: item.reason,
            fullReason: item.reason,
            addedByName: item.addedByName,
            createdAt: item.createdAt
              ? new Date(item.createdAt).toLocaleString()
              : "-",
          });
        });
        this.items = tempitems;
        this.totalRows = response.data.query.total;
      }
    },
    editIp(data) {
      this.modaltitle = "Edit IP";
      this.showAddIp = true;
      this.editMode = true;
      this.selectIpId = data.id;
      this.getIpDetails(this.selectIpId);
    },
    async getIpDetails(id) {
      const response = await this.$store.dispatch(GET_IP_BLOCKLIST_DETAILS, id);
      if (response.data && response.data.code === SUCCESS_CODE) {
        this.newIp.ip = response.data.details.cidr;
        this.newIp.reason = response.data.details.reason;
      } else {
        myFunction.showInfo(
          response.data?.message || "Failed to fetch IP details.",
          "error",
        );
      }
    },
    async updateIp() {
      this.isSubmitting = true;
      const params = {
        id: this.selectIpId,
        ip: this.newIp.ip,
        reason: this.newIp.reason,
      };
      this.$store
        .dispatch(UPDATE_IP_BLOCKLIST, params)
        .then((response) => {
          if (response.status == 200 && response.data.code === SUCCESS_CODE) {
            myFunction.showInfo("IP updated successfully.", "success");
            this.resetForm();
            this.showAddIp = false;
            this.$store.commit(ISLOADING, true);
            this.getIpList();
          } else {
            this.isSubmitting = false;
            myFunction.showInfo(
              response.data?.message || "Failed to update IP.",
              "error",
            );
          }
        })
        .catch((error) => {
          console.error("Error updating IP:", error);
          this.isSubmitting = false;
          myFunction.showInfo(
            "An error occurred while updating the IP. Please try again.",
            "error",
          );
        });
    },
    deleteIp(data) {
      const swalWithBootstrapButtons = this.$swal.mixin({
        customClass: {
          confirmButton: "btn btn-primary btn-lg my-3",
          cancelButton: "btn btn-outline-secondary btn-lg",
        },
        buttonsStyling: false,
      });
      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          text: `Remove ${data.cidr} from the blocklist?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
        })
        .then((result) => {
          if (result.isConfirmed) {
            let params = data.id;
            this.$store
              .dispatch(DELETE_IP_BLOCKLIST, params)
              .then((response) => {
                if (
                  response.status == 200 &&
                  response.data.code === SUCCESS_CODE
                ) {
                  myFunction.showInfo("IP removed from blocklist.", "success");
                  this.$store.commit(ISLOADING, true);
                  this.getIpList();
                } else {
                  myFunction.showInfo(
                    response.data?.message || "Failed to delete IP.",
                    "error",
                  );
                }
              })
              .catch((error) => {
                console.error("Error deleting IP:", error);
                myFunction.showInfo(
                  "An error occurred while deleting the IP. Please try again.",
                  "error",
                );
              });
          }
        });
    },
    onEnter(e) {
      const paginationMenu = Math.ceil(this.totalRows / this.perPage);
      if (e.target.value > 0) {
        if (paginationMenu >= e.target.value) {
          this.currentPage = this.currentPageJump = Number(e.target.value);
        } else {
          this.currentPage = this.currentPageJump = paginationMenu;
        }
      } else {
        this.currentPage = this.currentPageJump = 1;
      }
    },
  },
  async mounted() {
    this.$store.commit(ISLOADING, true);
    await this.getIpList();
  },
  watch: {
    currentPage: {
      handler: function () {
        this.$store.commit(ISLOADING, true);
        this.getIpList();
      },
    },
    perPage: {
      handler: function () {
        this.$store.commit(ISLOADING, true);
        this.getIpList();
      },
    },
  },
  computed: {
    ...mapGetters(["isLoading"]),
  },
};
</script>

<style scoped>
.table-custom th,
.table-custom td {
  vertical-align: middle;
}
.cursor-pointer {
  cursor: pointer;
}
.ip-empty-state__icon {
  font-size: 2.5rem;
  color: #adb5bd;
}
</style>
